/**
 * Os três cards de leitura da carteira: Leitura do Analista (texto montado
 * pelas regras a partir dos números, com atalhos de filtro e as 3 ações de
 * maior risco), Saúde da carteira e Próximas entregas.
 */
import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

import { plural } from "../../../../utils/dinheiro";
import IconeDaIA from "../../../CentralProjetos/IconeDaIA";
import { BarraCrescente } from "../comum/Graficos";
import { CURVA_ENTRADA, DURACAO, ENTRADA_DE_CARD, transicaoDeCard } from "../comum/movimento";
import { FILTROS_RAPIDOS } from "../dados/filtros";

const COR_DA_FAIXA = { ok: "var(--ok)", warn: "var(--warn)", bad: "var(--bad)" };
const COR = { ok: "var(--ok)", warn: "var(--warn)", bad: "var(--bad)" };
const RAIO_DONUT = 46;
const CIRC_DONUT = 2 * Math.PI * RAIO_DONUT;
const VAO_ENTRE_FATIAS = 3;

function Card({ indice, className = "", children, rotulo }) {
  return (
    <motion.section
      className={`cl-card ${className}`}
      aria-label={rotulo}
      initial={ENTRADA_DE_CARD.inicial}
      animate={ENTRADA_DE_CARD.final}
      transition={transicaoDeCard(indice, 0.12)}
    >
      {children}
    </motion.section>
  );
}

function Atalho({ id, rotulo, total, rapido, aoFiltrar }) {
  return (
    <button type="button" className="cl-chip" aria-pressed={rapido === id} onClick={() => aoFiltrar(id)}>
      {rotulo} <b>{total}</b>
    </button>
  );
}

function TextoDaLeitura({ leitura }) {
  const { atrasados, entregasProximas, pior, esperamEquipe } = leitura;
  return (
    <p className="cl-jev-p">
      {atrasados > 0 ? (
        <><b>{plural(atrasados, "cliente", "clientes")}</b> {atrasados === 1 ? "está" : "estão"} com entrega vencida ou atraso acumulado</>
      ) : (
        <>Nenhum cliente em andamento está com entrega vencida ou atraso acumulado</>
      )}
      {" "}e <b>{plural(entregasProximas, "entrega vence", "entregas vencem")}</b> nos próximos 7 dias.
      {pior && (
        <>
          {" "}O maior risco é <span className="cl-r">{pior.nome}</span>, com <b>{plural(pior.dias, "dia", "dias")}</b> de atraso
          acumulado{pior.esperaEquipe ? " e o cliente esperando retorno da equipe" : ""}.
        </>
      )}
      {esperamEquipe != null && esperamEquipe > 0 && (
        <> Em <b>{plural(esperamEquipe, "cliente", "clientes")}</b> a próxima ação é nossa.</>
      )}
    </p>
  );
}

export function LeituraDoAnalista({ leitura, comCentral, rapido, aoFiltrar, aoAbrir }) {
  return (
    <Card indice={0} className="cl-jev" rotulo="Leitura do Analista">
      <div className="cl-card-h">
        <div className="cl-jev-b">
          <span className="cl-jev-i" aria-hidden="true"><IconeDaIA tamanho={16} /></span>
          <div className="cl-jev-t">
            Leitura do Analista
            <small>agora · {comCentral ? "planilha + conversas" : "planilha"}</small>
          </div>
        </div>
        {leitura.atrasados > 0 && <span className="cl-pill p-bad"><span>{leitura.atrasados} em atraso</span></span>}
      </div>
      <TextoDaLeitura leitura={leitura} />
      <div className="cl-jev-a">
        <Atalho id={FILTROS_RAPIDOS.ATRASO} rotulo="Ver atrasados" total={leitura.atrasados} rapido={rapido} aoFiltrar={aoFiltrar} />
        <Atalho id={FILTROS_RAPIDOS.ENTREGA_PROXIMA} rotulo="Entregas em 7 dias" total={leitura.entregasProximas}
          rapido={rapido} aoFiltrar={aoFiltrar} />
        {comCentral && (
          <Atalho id={FILTROS_RAPIDOS.ESPERAM_EQUIPE} rotulo="Esperam a equipe" total={leitura.esperamEquipe}
            rapido={rapido} aoFiltrar={aoFiltrar} />
        )}
      </div>
      {leitura.sugestoes.length > 0 && (
        <div className="cl-sug-l">
          <span className="cl-eyebrow">Próximas ações sugeridas</span>
          {leitura.sugestoes.map((s) => (
            <Link key={s.id} to={`/legado/clientes/${s.id}`} className="cl-sug" onClick={(e) => aoAbrir(e, s.id)}>
              <span className="cl-sug-n">{s.ordem}</span>
              <span className="cl-sug-t" title={`${s.nome} — ${s.acao}`}><b>{s.nome}</b> — {s.acao}</span>
              <span className="cl-pill sm p-neu"><span>{s.dono}</span></span>
            </Link>
          ))}
        </div>
      )}
    </Card>
  );
}

function Donut({ saude }) {
  const mediaVisivel = saude.media == null ? "—" : `${saude.media}%`;
  let acumulado = 0;
  const fatias = saude.fatias.map((f) => {
    const comprimento = saude.total ? (f.total / saude.total) * CIRC_DONUT : 0;
    const fatia = { ...f, comprimento: Math.max(0, comprimento - VAO_ENTRE_FATIAS), inicio: acumulado };
    acumulado += comprimento;
    return fatia;
  });
  return (
    <div className="cl-donut" role="img"
      aria-label={`Saúde média ${mediaVisivel}: ${saude.fatias.map((f) => `${f.total} ${f.rotulo.toLowerCase()}`).join(", ")}`}>
      <svg width="112" height="112" viewBox="0 0 112 112" aria-hidden="true">
        <circle cx="56" cy="56" r={RAIO_DONUT} style={{ stroke: "var(--surface-3)" }} />
        {fatias.map((f, i) => f.total > 0 && (
          <motion.circle
            key={f.faixa}
            cx="56" cy="56" r={RAIO_DONUT}
            style={{ stroke: COR_DA_FAIXA[f.faixa], strokeDashoffset: -f.inicio }}
            initial={{ strokeDasharray: `0 ${CIRC_DONUT}` }}
            animate={{ strokeDasharray: `${f.comprimento} ${CIRC_DONUT}` }}
            transition={{ duration: DURACAO.barras, ease: CURVA_ENTRADA, delay: 0.25 + i * 0.08 }}
          />
        ))}
      </svg>
      <div className="cl-donut-c"><b>{mediaVisivel}</b><span>saúde média</span></div>
    </div>
  );
}

export function SaudeDaCarteira({ saude, esperas }) {
  return (
    <Card indice={1} rotulo="Saúde da carteira">
      <div className="cl-card-h">
        <span className="cl-eyebrow">Saúde da carteira</span>
        <span className="cl-mono">{plural(saude.total, "cliente", "clientes")}</span>
      </div>
      <div className="cl-donut-w">
        <Donut saude={saude} />
        <div className="cl-leg">
          {saude.fatias.map((f) => (
            <div key={f.faixa} className="cl-leg-i">
              <i style={{ background: COR_DA_FAIXA[f.faixa] }} />
              <span>{f.rotulo}</span>
              <b>{f.total}</b>
            </div>
          ))}
        </div>
      </div>
      {esperas && (
        <div className="cl-waits">
          <span className="cl-eyebrow">Quem está esperando quem</span>
          {esperas.map((e, i) => (
            <div key={e.espera} className="cl-wb">
              <span className="cl-wb-l">{e.rotulo}</span>
              <span className="cl-wb-t" role="img" aria-label={`${e.rotulo}: ${e.total}`}>
                <BarraCrescente proporcao={e.proporcao} style={{ background: COR[e.cor] }} atraso={0.3 + i * 0.06} />
              </span>
              <b>{e.total}</b>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export function ProximasEntregas({ entregas, aoAbrir }) {
  return (
    <Card indice={2} rotulo="Próximas entregas">
      <div className="cl-card-h">
        <span className="cl-eyebrow">Próximas entregas</span>
        <span className="cl-mono">14 dias</span>
      </div>
      <div className="cl-up-l">
        {entregas.length === 0 && <div className="cl-vazio-card">Nenhuma entrega nos próximos 14 dias.</div>}
        {entregas.map((u) => (
          <Link key={u.id} to={`/legado/clientes/${u.id}`} className="cl-up" onClick={(e) => aoAbrir(e, u.id)}>
            <span className="cl-up-d"><b>{u.dia}</b><span>{u.mes}</span></span>
            <span style={{ minWidth: 0 }}>
              <span className="cl-up-n">{u.nome}</span>
              <span className="cl-up-s">{u.detalhe}</span>
            </span>
            <span className={`cl-due ${u.tom}`}><span>{u.prazo}</span></span>
          </Link>
        ))}
      </div>
    </Card>
  );
}
